import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { UseTableConfigReturn, TABLE_CONSTANTS } from '../types';

// 🎯 Single Responsibility Principle (SRP)
// Este hook SOLO maneja la configuración de columnas y responsive behavior

interface UseTableConfigOptions {
  mobileBreakpoint?: number;
  headerCharWidth?: number;
  headerPadding?: number;
}

export const useTableConfig = (
  columns: ColumnDef<any>[], 
  containerWidth: number,
  options: UseTableConfigOptions = {}
): UseTableConfigReturn => {
  const {
    mobileBreakpoint = TABLE_CONSTANTS.MOBILE_BREAKPOINT,
    headerCharWidth = TABLE_CONSTANTS.HEADER_CHAR_WIDTH,
    headerPadding = TABLE_CONSTANTS.HEADER_PADDING
  } = options;

  return React.useMemo(() => {
    const isMobile = containerWidth > 0 && containerWidth < mobileBreakpoint;
    const totalProportions = columns.reduce((sum, col) => sum + (col.size || 100), 0);
    
    if (isMobile) {
      // 🎯 Configuración móvil: anchos fijos en píxeles
      const columnsWithFixedWidth = columns.map(col => {
        const headerText = String(col.header || '');
        const headerWidth = headerText.length * headerCharWidth + headerPadding;
        
        const size = col.size || 100;
        
        // 🎯 Cálculo inteligente de ancho mínimo basado en tamaño
        let contentMinWidth: number;
        
        if (size <= 15) contentMinWidth = 100;
        else if (size <= 25) contentMinWidth = 150;
        else if (size <= 50) contentMinWidth = 200;
        else if (size <= 75) contentMinWidth = 250;
        else contentMinWidth = 300;
        
        const calculatedWidth = Math.max(headerWidth, contentMinWidth);
        
        return { 
          ...col, 
          calculatedWidth 
        };
      });
      
      const tableWidth = columnsWithFixedWidth.reduce(
        (sum, col) => sum + col.calculatedWidth, 
        0
      );
      
      return {
        isMobile: true,
        columns: columnsWithFixedWidth,
        tableWidth,
        needsScroll: tableWidth > containerWidth,
      };
    }

    // 🎯 Configuración desktop: porcentajes relativos
    const columnsWithPercentages = columns.map(col => ({
      ...col,
      calculatedWidth: (col.size || 100) / totalProportions * 100,
    }));
    
    return {
      isMobile: false,
      columns: columnsWithPercentages,
      tableWidth: '100%',
      needsScroll: false,
    };
  }, [columns, containerWidth, mobileBreakpoint, headerCharWidth, headerPadding]);
}; 